/**
 * ENG-9585: host-side knowledge content cache, and the resolver that turns
 * `/host/refresh`'s `knowledge_manifest` back into the full entries the
 * provisioner expects.
 *
 * WHAT THIS BUYS. The poll used to carry every knowledge body to every agent
 * every ~10s so the manager could render one artifact
 * (`.claude/skills/core-knowledge/SKILL.md`) and then discover, by hash, that
 * it was identical to the file already on disk. Measured on prod: 17.0% of the
 * average `/host/refresh` body, 2,481,392 bytes on the largest org. Now the
 * poll carries `{ title, slug, scope, hash }` and the bodies are fetched once
 * and kept.
 *
 * CONTENT-ADDRESSED, AND HOST-WIDE. The store is keyed on the content hash
 * alone, not on agent, slug or scope, so it dedupes three ways at once: across
 * every agent on the host (platform-global and org knowledge are byte-identical
 * for all of them, so the first agent's fetch serves the rest), across scopes
 * (the same body published at team and org level is stored once), and across
 * time (an entry that is renamed, or deactivated and restored, is never
 * refetched). It lives at `~/.augmented/_knowledge/`; the `_` prefix is what
 * keeps `getRegisteredAgents` from mistaking it for an agent directory.
 *
 * METADATA IS NEVER CACHED. Only bodies are. `title` and `scope` are
 * interpolated into the rendered skill's section headers, so a retitled entry
 * must re-render even though its body — and therefore its hash — has not
 * changed. Taking metadata fresh from the manifest on every poll makes that
 * correct by construction; caching it alongside the body would make it correct
 * only for as long as someone remembers to invalidate on a rename.
 */

import { createHash } from 'node:crypto';
import {
  existsSync,
  mkdirSync,
  readFileSync,
  readdirSync,
  renameSync,
  rmSync,
  statSync,
  utimesSync,
  writeFileSync,
} from 'node:fs';
import { join } from 'node:path';

/** One entry as `/host/refresh` lists it in manifest mode. */
export interface KnowledgeManifestEntry {
  title: string;
  slug: string;
  scope: 'org' | 'team' | 'global';
  hash?: string;
}

/** A fully-resolved entry, the shape the provisioner has always consumed. */
export interface ResolvedKnowledgeEntry {
  title: string;
  slug: string;
  scope: 'org' | 'team' | 'global';
  content: string | null;
}

/**
 * Hashes per fetch request. Must not exceed the API's `MAX_KNOWLEDGE_FETCH`
 * (`packages/api/src/routes/host-runtime.ts`), which rejects a larger request
 * with a 400.
 */
export const KNOWLEDGE_FETCH_CHUNK = 100;

/** Untouched-for-this-long cached bodies are swept. A wrong sweep costs one refetch. */
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** At most one sweep per process per day — this runs inside a ~10s poll loop. */
const SWEEP_INTERVAL_MS = 24 * 60 * 60 * 1000;
let lastSweepAt = 0;

/**
 * sha256 of a body, matching what Postgres generates for the manifest
 * (migration 20260830000001).
 *
 * sha256 and not md5 (CodeRabbit, PR #5147) because THIS CACHE IS SHARED ACROSS
 * TENANTS — one store serves every agent on the host, which is most of what
 * makes it worth having. A collision therefore serves one org's agent another
 * org's body, and md5 collisions are cheaply constructible by anyone who can
 * author knowledge.
 *
 * UTF-8 explicitly, not the platform default: Postgres digests
 * `compressed_content::bytea`, i.e. the UTF-8 bytes, and knowledge is prose
 * full of em dashes and smart quotes that are one code unit but three bytes. A
 * mismatched encoding here would verify clean on ASCII entries and reject
 * exactly the text-heavy ones.
 */
export function knowledgeHash(content: string): string {
  return createHash('sha256').update(content, 'utf8').digest('hex');
}

function cachePath(cacheDir: string, hash: string): string {
  return join(cacheDir, hash);
}

/**
 * Read a cached body, or null.
 *
 * Touches the file's mtime on a hit so the sweep below measures LAST USE rather
 * than last write — otherwise a body that is read on every poll but never
 * rewritten would age out and be refetched for no reason.
 */
export function readCachedKnowledge(cacheDir: string, hash: string): string | null {
  const path = cachePath(cacheDir, hash);
  try {
    const content = readFileSync(path, 'utf-8');
    try {
      const now = new Date();
      utimesSync(path, now, now);
    } catch { /* touch is an optimisation, never a failure */ }
    return content;
  } catch {
    return null;
  }
}

/**
 * Store a body under its hash, but only if it actually hashes to that.
 *
 * The verification is the point: it is what makes the cache safe to trust on
 * every subsequent poll without re-checking. A truncated or mis-mapped response
 * is rejected here — where it costs one refetch — instead of being written once
 * and then served as authoritative content for as long as the file survives.
 * Returns whether the body was accepted.
 */
export function writeCachedKnowledge(cacheDir: string, hash: string, content: string): boolean {
  if (knowledgeHash(content) !== hash) return false;
  try {
    mkdirSync(cacheDir, { recursive: true });
    // Write-then-rename: a manager killed mid-write must not leave a truncated
    // body at a name that claims to be a verified hash.
    const tmp = `${cachePath(cacheDir, hash)}.tmp-${process.pid}`;
    writeFileSync(tmp, content, 'utf-8');
    renameSync(tmp, cachePath(cacheDir, hash));
    return true;
  } catch {
    return false;
  }
}

/** Drop bodies untouched for CACHE_TTL_MS. Best-effort; rate-limited per process. */
export function sweepKnowledgeCache(cacheDir: string, now = Date.now()): void {
  if (now - lastSweepAt < SWEEP_INTERVAL_MS) return;
  lastSweepAt = now;
  try {
    if (!existsSync(cacheDir)) return;
    for (const name of readdirSync(cacheDir)) {
      const path = join(cacheDir, name);
      try {
        if (now - statSync(path).mtimeMs > CACHE_TTL_MS) rmSync(path, { force: true });
      } catch { /* a body we cannot stat or remove is not worth failing a poll over */ }
    }
  } catch { /* non-fatal */ }
}

/** Resets the sweep rate-limiter. Test seam only. */
export function __resetSweepClock(): void {
  lastSweepAt = 0;
}

export interface ResolveKnowledgeArgs {
  manifest: KnowledgeManifestEntry[];
  cacheDir: string;
  /**
   * The agent's `knowledge_delivery`. A `search` agent reaches knowledge
   * through MCP tools and the adapter renders no file for it, so its bodies are
   * never needed — this skips the fetch entirely rather than downloading
   * content that is filtered out two calls later.
   */
  delivery: 'search' | 'files' | 'both';
  /** Fetches bodies for the given hashes. Chunked by the caller of this module. */
  fetchContents: (hashes: string[]) => Promise<Array<{ hash: string; content: string }>>;
  log?: (msg: string) => void;
}

/**
 * Turn a manifest into the entries the provisioner consumes, fetching only the
 * bodies this host does not already have.
 *
 * Fails SOFT, deliberately. An entry whose body cannot be resolved comes back
 * with `content: null` and the caller's existing `!!k.content` filter drops it,
 * exactly as it already drops a row whose compression never completed. The
 * alternative — throwing — would take down the whole poll for one unreachable
 * body, and the poll also carries channel config, secrets and scheduled tasks.
 * The cost of failing soft is a core-knowledge skill that is briefly missing a
 * section and self-heals on the next poll; the cost of failing hard is an agent
 * that stops being reconciled at all.
 */
export async function resolveKnowledgeFromManifest(
  args: ResolveKnowledgeArgs,
): Promise<ResolvedKnowledgeEntry[]> {
  const { manifest, cacheDir, delivery, fetchContents, log } = args;

  const meta = (e: KnowledgeManifestEntry) => ({ title: e.title, slug: e.slug, scope: e.scope });

  // `search` agents get metadata only. The adapter emits no knowledge file for
  // them, so a body fetched here would be discarded unread.
  //
  // The sweep still runs (CodeRabbit, PR #5147): returning above it meant a
  // host whose agents are ALL search-delivery never expired anything, so a
  // cache built up before the delivery mode was switched would sit there for
  // the life of the host.
  if (delivery === 'search') {
    sweepKnowledgeCache(cacheDir);
    return manifest.map((e) => ({ ...meta(e), content: null }));
  }

  const bodies = new Map<string, string>();
  const missing: string[] = [];
  for (const entry of manifest) {
    if (!entry.hash || bodies.has(entry.hash)) continue;
    const cached = readCachedKnowledge(cacheDir, entry.hash);
    if (cached !== null) bodies.set(entry.hash, cached);
    else if (!missing.includes(entry.hash)) missing.push(entry.hash);
  }

  if (missing.length > 0) {
    for (let i = 0; i < missing.length; i += KNOWLEDGE_FETCH_CHUNK) {
      const chunk = missing.slice(i, i + KNOWLEDGE_FETCH_CHUNK);
      try {
        for (const { hash, content } of await fetchContents(chunk)) {
          // CodeRabbit, PR #5147. The first draft cached only what verified but
          // SERVED the body either way, reasoning that it was still the poll's
          // best answer. That was wrong about what a mismatch means: the two
          // ways to get here are transport corruption and a server-side
          // hash->body mapping bug, and the second one hands back ANOTHER
          // ENTRY'S content. Rendering that into the agent's knowledge is worse
          // than rendering nothing — a missing section is visibly missing and
          // self-heals on the next poll, whereas wrong-but-plausible knowledge
          // is neither. So an unverified body is dropped, not served.
          if (!writeCachedKnowledge(cacheDir, hash, content)) {
            log?.(`[knowledge] hash mismatch for ${hash} — dropping this poll`);
            continue;
          }
          bodies.set(hash, content);
        }
      } catch (err) {
        log?.(`[knowledge] fetch failed for ${chunk.length} entries: ${(err as Error).message}`);
      }
    }
  }

  sweepKnowledgeCache(cacheDir);

  return manifest.map((e) => ({
    ...meta(e),
    content: (e.hash ? bodies.get(e.hash) : undefined) ?? null,
  }));
}
